import { Suspense } from 'react';

import { IconDownload } from '@iwoca/icons';
import { fetchGetLoanDocuments } from '@iwoca/lapi-client/edge';
import { Button, Skeleton } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import {
    H1,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@ui';
import { FormattedMessage, useIntl } from 'react-intl';

import messages from './contracts.messages';
import styles from './contracts.module.css';
import { useStateKey } from '../../iwQueryClient/useStateKey';

export const Contracts = () => {
    return (
        <div className={styles.contractsPage} data-testid="contracts_page">
            <H1>
                <FormattedMessage {...messages.title} />
            </H1>

            <Table className="mx-auto my-0 max-w-[800px] text-m">
                <TableHeader>
                    <TableRow className="flex">
                        <TableHead className="w-full">
                            <FormattedMessage {...messages.dateColHeader} />
                        </TableHead>
                        <TableHead className="w-full">
                            <FormattedMessage {...messages.typeColHeader} />
                        </TableHead>
                        <TableHead className="w-full" />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <Suspense
                        fallback={
                            <>
                                <SkeletonRow />
                                <SkeletonRow />
                                <SkeletonRow />
                            </>
                        }
                    >
                        <ContractsTableRows />
                    </Suspense>
                </TableBody>
            </Table>
        </div>
    );
};

const useContracts = () => {
    const intl = useIntl();
    const stateKey = useStateKey();

    const translateDocumentType = (documentType: string) => {
        if (documentType === 'agreement') {
            return intl.formatMessage(messages.agreement);
        }

        if (documentType === 'guarantee') {
            return intl.formatMessage(messages.guarantee);
        }

        return intl.formatMessage(messages.otherDoc);
    };

    const { data: contracts } = useQuery({
        suspense: true,
        queryKey: ['fetchGetLoanDocuments', stateKey],
        queryFn: () => fetchGetLoanDocuments({ stateKey }),
        select: ({ data }) =>
            data.document_list.map((document) => {
                return {
                    id: document.document_id,
                    title: document.document_title,
                    documentType: translateDocumentType(document.document_type),
                    dateSigned: intl.formatDate(document.date_signed, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                    }),
                    downloadUrl: `/api/lending/edge/loan_documents/${stateKey}/signed/${document.document_id}/?file_format=pdf`
                };
            })
    });

    return { contracts: contracts! };
};

const ContractsTableRows = () => {
    const { contracts } = useContracts();

    return (
        <>
            {contracts.map(
                ({ dateSigned, documentType, title, id, downloadUrl }) => (
                    <TableRow key={id} className="flex min-w-[450px]">
                        <TableCell className="flex w-full">
                            {dateSigned}
                        </TableCell>
                        <TableCell className="flex w-full">
                            <div className="flex flex-col">
                                <span>{documentType}</span>

                                <span className="text-s text-structure-40">
                                    {title}
                                </span>
                            </div>
                        </TableCell>
                        <TableCell className="flex w-full justify-end">
                            <Button download href={downloadUrl}>
                                <FormattedMessage {...messages.download} />
                                <IconDownload />
                            </Button>
                        </TableCell>
                    </TableRow>
                )
            )}
        </>
    );
};

const SkeletonRow = () => (
    <TableRow className="flex w-full">
        <TableCell className="flex w-full justify-center">
            <Skeleton />
        </TableCell>
        <TableCell className="flex w-full justify-center">
            <Skeleton />
        </TableCell>
        <TableCell className="flex w-full justify-center">
            <Skeleton />
        </TableCell>
    </TableRow>
);
